/**
 * Circuit Culture promo / announcement bar.
 * - Rotates messages vertically (bottom-up), one at a time.
 * - Pause / play button stops and resumes the rotation.
 * - Dismiss (X) hides the bar for the rest of the browsing session.
 * - Pauses on hover and when the tab is hidden.
 * - Respects prefers-reduced-motion (cross-fades instead of sliding).
 *
 * Progressive enhancement: without JS the first message shows statically.
 */
const STORAGE_KEY = 'cc-promo-bar-dismissed';
const SLIDE_MS = 600;

class PromoBar extends HTMLElement {
  #slides = [];
  #current = 0;
  #timer = null;
  #userPaused = false;
  #reduceMotion = false;

  connectedCallback() {
    // Respect a prior dismissal for this session.
    try {
      if (sessionStorage.getItem(STORAGE_KEY) === '1') {
        this.hidden = true;
        return;
      }
    } catch (e) {
      /* sessionStorage may be unavailable (private mode) — ignore */
    }

    this.#reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const track = this.querySelector('[data-promo-track]');
    this.#slides = track ? Array.from(track.querySelectorAll('.announcement-bar__slide')) : [];

    // Set the first message active before enabling the JS-driven layout so a
    // mid-init failure can't leave the bar empty.
    this.#slides.forEach((slide, index) => {
      slide.classList.toggle('is-active', index === 0);
      slide.classList.remove('is-leaving', 'no-transition');
      slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
    });
    this.classList.add('is-ready');

    const closeBtn = this.querySelector('[data-promo-close]');
    if (closeBtn) closeBtn.addEventListener('click', () => this.#dismiss());

    const pauseBtn = this.querySelector('[data-promo-pause]');
    if (pauseBtn) pauseBtn.addEventListener('click', () => this.#togglePause(pauseBtn));

    this.addEventListener('mouseenter', this.#stop);
    this.addEventListener('mouseleave', this.#resume);
    document.addEventListener('visibilitychange', this.#handleVisibility);

    this.#start();
  }

  disconnectedCallback() {
    this.#stop();
    document.removeEventListener('visibilitychange', this.#handleVisibility);
  }

  get #interval() {
    const seconds = parseFloat(this.getAttribute('data-speed') || '7');
    return (Number.isFinite(seconds) ? seconds : 7) * 1000;
  }

  #start = () => {
    if (this.#userPaused || this.#slides.length < 2 || this.#timer) return;
    this.#timer = window.setInterval(this.#advance, this.#interval);
  };

  #stop = () => {
    if (this.#timer) {
      window.clearInterval(this.#timer);
      this.#timer = null;
    }
  };

  #resume = () => {
    if (!this.#userPaused) this.#start();
  };

  #handleVisibility = () => {
    if (document.hidden) this.#stop();
    else this.#resume();
  };

  #togglePause = (button) => {
    this.#userPaused = !this.#userPaused;
    this.toggleAttribute('data-paused', this.#userPaused);
    button.setAttribute('aria-pressed', String(this.#userPaused));
    if (this.#userPaused) this.#stop();
    else this.#start();
  };

  #advance = () => {
    const total = this.#slides.length;
    if (total < 2) return;

    const leaving = this.#slides[this.#current];
    const nextIndex = (this.#current + 1) % total;
    const entering = this.#slides[nextIndex];

    leaving.classList.remove('is-active');
    leaving.classList.add('is-leaving');
    leaving.setAttribute('aria-hidden', 'true');

    entering.classList.remove('no-transition', 'is-leaving');
    entering.classList.add('is-active');
    entering.setAttribute('aria-hidden', 'false');

    // Once it has slid out the top, silently reset it to the waiting position
    // below so it can rise again on its next turn.
    window.setTimeout(() => {
      leaving.classList.add('no-transition');
      leaving.classList.remove('is-leaving');
      void leaving.offsetHeight; // force reflow so the reset isn't animated
      leaving.classList.remove('no-transition');
    }, this.#reduceMotion ? 0 : SLIDE_MS);

    this.#current = nextIndex;
  };

  #dismiss = () => {
    this.#stop();
    this.hidden = true;
    try {
      sessionStorage.setItem(STORAGE_KEY, '1');
    } catch (e) {
      /* ignore */
    }
  };
}

if (!customElements.get('promo-bar')) {
  customElements.define('promo-bar', PromoBar);
}
